import { useEffect, useRef } from 'react'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { getCurrentPosition } from '@utils'
import { reverseGeocode } from '../utils/reverseGeocode'
import { useFtUpdateLocation } from './api/mutations/ft-location/useFtUpdateLocation'
import { useGetNearbyVisibility } from './api/queries/useGetNearbyVisibility'

const TRACK_KEY = 'ft_last_location_track'
const THROTTLE_MS = 30 * 60 * 1000 // 30 minutes

export const useTrackLocation = (isLoggedIn: boolean = false) => {
  const { mutate } = useFtUpdateLocation()
  // Only query visibility once logged in: this hook is mounted globally (WebLayout),
  // and an unauthenticated call 401s → interceptor signs out → login page redirects.
  const { data: visibilityData, isLoading: isVisibilityLoading } = useGetNearbyVisibility(isLoggedIn)
  const tracked = useRef(false)

  useEffect(() => {
    // Wait until we know the visibility scope before doing anything, so we never
    // publish coordinates before confirming the user has not opted out.
    if (!isLoggedIn || tracked.current || isVisibilityLoading) return

    // Privacy: if the user chose to stay invisible (HIDDEN = "not visible to
    // anyone"), never collect or publish their location.
    if (visibilityData?.scope === 'HIDDEN') {
      tracked.current = true
      return
    }

    tracked.current = true

    const track = async () => {
      // Throttle: skip if tracked recently
      const lastTrack = await AsyncStorage.getItem(TRACK_KEY)
      if (lastTrack && Date.now() - Number(lastTrack) < THROTTLE_MS) return

      const coords = await getCurrentPosition()
      if (!coords) return

      const geo = await reverseGeocode(coords.latitude, coords.longitude)

      mutate({
        latitude: coords.latitude,
        longitude: coords.longitude,
        city: geo.city,
        country: geo.country,
      })

      await AsyncStorage.setItem(TRACK_KEY, Date.now().toString())
    }

    track()
  }, [isLoggedIn, mutate, isVisibilityLoading, visibilityData?.scope])
}
